// Сетевой транспорт аналитики (E18-T02; tech/03 §17, Р-103): POST /analytics/batch. Обычная пачка — типизированным
// вызовом API (токен — в заголовке и в теле), при уходе в фон — navigator.sendBeacon: заголовков он не ставит, поэтому
// accessToken только в теле. 413 PAYLOAD_TOO_LARGE — пачка делится пополам; сбой сети — исключение (пачка вернётся в
// очередь и уйдёт снова с теми же eventId); прочие отказы — пачка снимается, чтобы не крутиться вечно.
import { API_PREFIX, type Api, type BodyOf } from '@garages/contract';
import type { App } from '../app/app.js';
import { BUILD_TAG } from '../app/app.js';
import { ApiCallError } from '../net/api-client.js';
import type { AnalyticsEvent, AnalyticsTransport } from './analytics.js';

type Body = BodyOf<Api['analyticsBatch']>;
type Prop = string | number | boolean | null;

/** Значения props по договору: строка ≤ 256, число, логическое, null; прочее — строкой JSON. Ключ ≤ 48. */
export function toProps(p: Record<string, unknown>): Record<string, Prop> {
  const out: Record<string, Prop> = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined) continue;
    const key = k.slice(0, 48);
    if (v === null || typeof v === 'boolean') out[key] = v;
    else if (typeof v === 'number') out[key] = Number.isFinite(v) ? v : null;
    else out[key] = (typeof v === 'string' ? v : JSON.stringify(v)).slice(0, 256);
  }
  return out;
}

export function envelope(sessionId: string, batch: AnalyticsEvent[], ctx: { platform: string; tgVersion?: string }, token: string | null): Body {
  return {
    sessionId,
    sentAt: new Date().toISOString(),
    ctx: { platform: ctx.platform.slice(0, 32), ...(ctx.tgVersion ? { tgVersion: ctx.tgVersion.slice(0, 16) } : {}), clientBuild: BUILD_TAG },
    ...(token ? { accessToken: token } : {}),
    events: batch.map((e) => ({ eventId: e.eventId, name: e.name, seq: e.seq, ts: new Date(e.ts).toISOString(), props: toProps(e.props) })),
  };
}

/** beacon(url, json) — navigator.sendBeacon; в режиме фикстур — fetch мок-сервера (sendBeacon мимо мока не пройдёт). */
export function createNetworkTransport(app: App, beaconImpl?: (url: string, json: string) => boolean): AnalyticsTransport {
  const ctx = { platform: `tma_${app.wa?.platform ?? 'web'}`, ...(app.wa?.version ? { tgVersion: app.wa.version } : {}) };
  const token = () => app.session.current()?.accessToken ?? null;
  const send = async (sessionId: string, batch: AnalyticsEvent[]): Promise<void> => {
    try {
      await app.call('analyticsBatch', { body: envelope(sessionId, batch, ctx, token()), noRetry: true });
    } catch (e) {
      if (!(e instanceof ApiCallError)) throw e;
      if (e.code === 'NETWORK' || e.code === 'TIMEOUT' || e.status >= 500 || e.status === 429) throw e;
      if (e.code === 'PAYLOAD_TOO_LARGE' && batch.length > 1) {
        const mid = Math.ceil(batch.length / 2);
        await send(sessionId, batch.slice(0, mid));
        await send(sessionId, batch.slice(mid));
      }
      // прочие 4xx — пачка битая: не повторяем
    }
  };
  const beacon = beaconImpl ?? ((url: string, json: string) => {
    try { return navigator.sendBeacon(url, new Blob([json], { type: 'application/json' })); } catch { return false; }
  });
  return {
    send,
    beacon: (sessionId, batch) => beacon(`${API_PREFIX}/analytics/batch`, JSON.stringify(envelope(sessionId, batch, ctx, token()))),
  };
}
